"use client";

import type { ProductDetails } from "@raumagarn/formatters";
import _isEqual from "lodash/isEqual";
import { useMemo, useState } from "react";
import { MicroCarts } from "~/features/cart/MicroCart/MicroCartContext";
import { useMicroCart } from "~/features/cart/MicroCart/useMicroCart";
import { getPrice, usePricelistId } from "~/lib/hooks/usePrice";
import { formatPrice } from "~/lib/utils/price";

export const RecipeType = {
	Recipe: "recipe",
	Magazine: "magazine",
} as const;
export type RecipeType = (typeof RecipeType)[keyof typeof RecipeType];

export const RecipeFormat = {
	Digital: "digital",
	Paper: "papir",
	DigitalAndPaper: "digital_og_papir",
} as const;
export type RecipeFormat = (typeof RecipeFormat)[keyof typeof RecipeFormat];

interface RecipeTypeOptions {
	defaultValues?: {
		format?: RecipeFormat;
	};
}

export const useRecipeTypePicker = (
	product: ProductDetails,
	{ defaultValues }: RecipeTypeOptions = {},
) => {
	const [type, setType] = useState<RecipeType>(RecipeType.Recipe);
	const [format, setFormat] = useState<RecipeFormat>(defaultValues?.format || RecipeFormat.Digital);

	const hasBooklet = !!product?.booklet?.displayItemId;

	const pricelistId = usePricelistId();
	const recipePrices = getPrice({ pricelist: product.pricelist, pricelistId });
	const bookletPrices = getPrice({
		pricelist: product?.booklet?.pricelist,
		pricelistId,
	});

	const options = useMemo(
		() => [
			{
				label: "Oppskrift",
				id: product?.size?.id,
				sku: product?.sku,
				name: product.name,
				prices: recipePrices,
				subtitle: `${formatPrice(recipePrices.price)}`,
				quantity: 1,
				value: RecipeType.Recipe,
				format: format,
				comment: format,
				selected: _isEqual(type, RecipeType.Recipe),
				stock: product?.size?.stock,
			},
			...(hasBooklet
				? [
						{
							label: "Hefte",
							id: product?.booklet?.size?.id,
							sku: product?.booklet?.sku,
							name: product?.booklet?.name,
							prices: bookletPrices,
							subtitle: `${formatPrice(bookletPrices.price)}`,
							quantity: 1,
							value: RecipeType.Magazine,
							format: format,
							comment: format,
							selected: _isEqual(type, RecipeType.Magazine),
							stock: product?.booklet?.size?.stock,
						},
					]
				: []),
		],
		[product, format, type, hasBooklet, bookletPrices, recipePrices],
	);

	const selected = options.find((option) => option.selected) || options[0];

	useMicroCart(MicroCarts.Recipe, [selected || {}], {
		id: "id",
		quantity: "quantity",
	});

	const onChangeFormat = (e: {
		target: {
			value: string;
		};
	}) => {
		setFormat(e.target.value as RecipeFormat);
	};

	const onChangeType = (e: {
		target: {
			value: string;
		};
	}) => {
		setType(e.target.value as RecipeType);
	};

	const states = {
		type: {
			value: type,
			set: onChangeType,
		},
		format: {
			value: format,
			set: onChangeFormat,
		},
	};

	return {
		...states,
		options,
		selected,
	};
};
